/**
 * useSeriesDetails.js - Custom hook for fetching series details
 *
 * This hook provides series data fetching functionality using the SpotlightService
 * for centralized API management and React 19 patterns.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppLoad } from '../contexts/AppLoadContext';
import { cacheService } from '../services/CacheService';
import spotlightService from '../services/SpotlightService';

/**
 * Hook for fetching and managing series details
 *
 * @param {string} seriesId - The series ID or slug to fetch
 * @returns {Object} Series data and state
 * @returns {Object} .series - The series data
 * @returns {Array} .childChannels - Child channels (seasons) for the series
 * @returns {Object} .currentChildChannel - Currently selected child channel
 * @returns {Array} .episodes - Episodes for the current child channel
 * @returns {Object} .selectedEpisode - Currently selected episode
 * @returns {Array} .relatedContent - Related content for the series
 * @returns {boolean} .loading - Whether data is currently loading
 * @returns {string} .error - Error message if fetch failed
 * @returns {Function} .retry - Function to retry fetching data
 * @returns {Function} .selectChildChannel - Function to select a child channel
 * @returns {Function} .selectEpisode - Function to select an episode
 */
export const useSeriesDetails = (seriesId, options = {}) => {
  const { autoSelectFirstEpisode = true, initialEpisodeId = null } = options;
  const [series, setSeries] = useState(null);
  const [parentChannel, setParentChannel] = useState(null);
  const [childChannels, setChildChannels] = useState([]);
  const [currentChildChannel, setCurrentChildChannel] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [relatedContent, setRelatedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the working AppLoadContext authentication
  const { apiUrl, token } = useAppLoad();

  // Track hook calls and parameter changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // useSeriesDetails CALL logged
    }
  }, [seriesId, apiUrl, token]);

  // Track state changes (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // useSeriesDetails state logged
    }
  }, [series, childChannels, currentChildChannel, episodes, selectedEpisode, loading, error]);

  // Process episodes when currentChildChannel changes
  const processEpisodes = useCallback(() => {
    if (!currentChildChannel) {
      return;
    }

    let episodesData = [];

    // Try to get episodes from currentChildChannel if available
    if (currentChildChannel && currentChildChannel.playlist) {
      // Ensure playlist is an array
      const playlist = Array.isArray(currentChildChannel.playlist)
        ? currentChildChannel.playlist
        : [];

      // Use the episode objects directly from the playlist array
      episodesData = playlist.map((item, idx) => {
        if (typeof item === 'object' && item !== null) {
          // Get thumbnail URL directly from thumb property
          let thumbnailUrl;

          if (item.thumb) {
            // Check if thumb is already a full URL or just an ID
            if (
              typeof item.thumb === 'string' &&
              (item.thumb.startsWith('http://') || item.thumb.startsWith('https://'))
            ) {
              // It's already a full URL, just append optimization parameters if needed
              thumbnailUrl = item.thumb.includes('?')
                ? `${item.thumb}&w=640&h=360&fit=crop`
                : `${item.thumb}?w=640&h=360&fit=crop`;
            } else {
              // It's just an ID, construct the full URL
              thumbnailUrl = `https://images.dotstudiopro.com/${item.thumb}?w=640&h=360&fit=crop`;
            }
          } else {
            thumbnailUrl = null;
          }

          // Create episode object with direct mapping from schema fields
          return {
            _id: item._id || `episode_${idx}_${Math.random().toString(36).substring(7)}`,
            title: item.title || `Episode ${idx + 1}`,
            episode: item.episode || idx + 1,
            description: item.description || 'Episode description will be available when playing.',
            thumbnail: thumbnailUrl,
            // Preserve video data for playback
            video: item || null,
          };
        } else {
          // If item is just an ID or primitive, create a basic episode object
          return {
            _id: `episode_${idx}_${item || Math.random().toString(36).substring(7)}`,
            title: `Episode ${idx + 1}`,
            episode: idx + 1,
            description: 'Episode description will be available when playing.',
            thumbnail: null,
          };
        }
      });
    }

    // If no episodes were found or processed, create a basic fallback episode
    if (!episodesData || episodesData.length === 0) {
      episodesData = [
        {
          _id: 'placeholder_episode',
          title: 'No episodes available',
          episode: 1,
          description: 'No episode data is currently available.',
          thumbnail: null,
        },
      ];
    }

    // Sort episodes by episode number if available
    episodesData.sort((a, b) => {
      if (a.episode && b.episode) {
        return parseInt(a.episode) - parseInt(b.episode);
      }
      return 0;
    });

    // Set episodes
    setEpisodes(episodesData);

    // Set the first episode as selected by default if none is selected and auto-selection is enabled
    if (episodesData.length > 0 && !selectedEpisode && autoSelectFirstEpisode) {
      setSelectedEpisode(episodesData[0]);
    } else if (episodesData.length > 0 && !selectedEpisode && !autoSelectFirstEpisode) {
      // Auto-select disabled — leave selectedEpisode unset intentionally.
    }
  }, [currentChildChannel, selectedEpisode, autoSelectFirstEpisode]);

  // Main data fetching function
  const fetchSeriesDetails = useCallback(async () => {
    if (!seriesId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Reset state when fetching new series data
      setSeries(null);
      setParentChannel(null);
      setChildChannels([]);
      setCurrentChildChannel(null);
      setEpisodes([]);
      setSelectedEpisode(null);
      setRelatedContent([]);

      if (!apiUrl || !token) {
        setError('API URL or token not available. App may still be loading.');
        setLoading(false);
        return;
      }

      // Use SpotlightService to get series details by slug

      const seriesData = await spotlightService.getChannelBySlug(seriesId);

      // Check if we have valid series data
      if (!seriesData || !seriesData.channels || !seriesData.channels.length) {
        throw new Error('Invalid series data received');
      }

      const fetchedParentChannel = seriesData.channels[0];

      setSeries(fetchedParentChannel);
      setParentChannel(fetchedParentChannel);

      // Check if this is a parent channel with child channels (seasons)
      if (fetchedParentChannel.childchannels && fetchedParentChannel.childchannels.length > 0) {
        const childChannelsData = fetchedParentChannel.childchannels;

        // Fetch full details for each child channel to get episode-level data (including bypass_channel_lock)
        const childChannelsWithDetails = await Promise.all(
          childChannelsData.map(async (childChannel) => {
            try {
              // Check if playlist is already populated in the initial response
              const hasPlaylist = childChannel.playlist && Array.isArray(childChannel.playlist) && childChannel.playlist.length > 0;
              
              // Only fetch child channel details if playlist is not already populated
              // This avoids unnecessary API calls and prevents overwriting existing playlist data
              let childDetails = null;
              if (!hasPlaylist) {
                try {
                  childDetails = await spotlightService.getChildChannelDetails(
                    seriesId,
                    childChannel.slug || childChannel._id,
                  );
                } catch (fetchError) {
                  console.warn(`Failed to fetch details for child channel ${childChannel.slug}:`, fetchError);
                }
              }
              
              // Merge the detailed data with the basic child channel data
              // Preserve playlist from childChannel if it exists, otherwise use childDetails playlist
              return {
                ...childChannel,
                ...(childDetails || {}),
                // Preserve the playlist from childChannel if it's already populated
                playlist: hasPlaylist ? childChannel.playlist : (childDetails?.playlist || childChannel.playlist),
                // Preserve the subscription_access from childchannels array if available
                subscription_access: childChannel.subscription_access || childDetails?.subscription_access,
              };
            } catch (error) {
              console.warn(`Failed to process child channel ${childChannel.slug}:`, error);
              // Return the basic child channel data if processing fails
              return childChannel;
            }
          }),
        );

        // Sort child channels (seasons) by title or other criteria
        childChannelsWithDetails.sort((a, b) => {
          // Try to extract season numbers from titles if available
          const seasonA = a.title.match(/season\s*(\d+)/i);
          const seasonB = b.title.match(/season\s*(\d+)/i);

          if (seasonA && seasonB) {
            return parseInt(seasonA[1]) - parseInt(seasonB[1]);
          }

          // Fall back to alphabetical sorting
          return a.title.localeCompare(b.title);
        });

        // Ensure each child channel has a unique, descriptive title for the dropdown
        childChannelsWithDetails.forEach((channel, index) => {
          // If the title doesn't already contain "Season", add season number
          if (!channel.title.match(/season\s*\d+/i)) {
            // Try to extract season number from other properties
            let seasonNumber = index + 1;

            // Check if there's a season property
            if (channel.season) {
              seasonNumber = channel.season;
            } else if (channel.title.match(/\d+/)) {
              // Try to extract a number from the title
              const match = channel.title.match(/\d+/);
              if (match) {
                seasonNumber = parseInt(match[0]);
              }
            }

            // Set a display title for the dropdown
            channel.displayTitle = `Season ${seasonNumber}`;
          } else {
            // If it already has "Season" in the title, use that
            channel.displayTitle = channel.title;
          }

          // Make sure we don't have duplicate display titles
          const duplicates = childChannelsWithDetails.filter(
            (c, i) => i < index && c.displayTitle === channel.displayTitle,
          );

          if (duplicates.length > 0) {
            channel.displayTitle = `${channel.displayTitle} (${index + 1})`;
          }
        });

        setChildChannels(childChannelsWithDetails);

        // Pick the season that contains the requested episode (initialEpisodeId)
        // if one is provided; otherwise default to the first season. Without this,
        // a deep-link to S2E4 would load S1 here and the episode lookup would fail.
        if (childChannelsWithDetails.length > 0) {
          let initialSeason = childChannelsWithDetails[0];
          if (initialEpisodeId) {
            const seasonWithEpisode = childChannelsWithDetails.find(cc => {
              const playlist = cc.playlist || cc.episodes || [];
              return Array.isArray(playlist) && playlist.some(ep => ep?._id === initialEpisodeId);
            });
            if (seasonWithEpisode) {initialSeason = seasonWithEpisode;}
          }
          setCurrentChildChannel(initialSeason);
        }
      } else if (
        Array.isArray(fetchedParentChannel.playlist) &&
        fetchedParentChannel.playlist.length > 0
      ) {
        // Single channel with no wrapper (e.g. an ICOD season sold on its own):
        // treat the channel as its own single season so the episode list and
        // its per-episode locks render the same as a parent channel's season.
        const singleSeason = {
          ...fetchedParentChannel,
          displayTitle: fetchedParentChannel.title,
        };
        setChildChannels([singleSeason]);
        setCurrentChildChannel(singleSeason);
      }

      // Fetch related content using SpotlightService
      try {
        const relatedData = await spotlightService.getRelatedChannelsByChannelSlug(seriesId);

        if (relatedData && relatedData.channels && Array.isArray(relatedData.channels)) {
          setRelatedContent(relatedData.channels);
        } else {
          setRelatedContent([]);
        }
      } catch (relatedError) {
        console.warn('useSeriesDetails: Failed to fetch related content:', relatedError);
        setRelatedContent([]);
      }
    } catch (err) {
      console.error('Error fetching series details:', err);
      setError(`Failed to load series: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [seriesId, apiUrl, token]);

  // Function to select a child channel (season)
  const selectChildChannel = useCallback(channel => {
    setCurrentChildChannel(channel);
  }, []);

  // Function to select an episode
  const selectEpisode = useCallback(episode => {
    setSelectedEpisode(episode);
  }, []);

  // Function to retry fetching data
  const retry = useCallback(() => {
    fetchSeriesDetails();
  }, []); // Remove fetchSeriesDetails dependency

  // Fetch data on mount and when seriesId changes
  useEffect(() => {
    if (apiUrl && token) {
      fetchSeriesDetails();
    }
  }, [apiUrl, token, seriesId]); // Remove fetchSeriesDetails dependency

  // Process episodes when currentChildChannel changes
  useEffect(() => {
    if (currentChildChannel) {
      processEpisodes();
    }
  }, [currentChildChannel]); // Remove processEpisodes dependency

  return {
    series,
    childChannels,
    currentChildChannel,
    episodes,
    selectedEpisode,
    relatedContent,
    loading,
    error,
    retry,
    selectChildChannel,
    selectEpisode,
  };
};

export default useSeriesDetails;
